/**
 * What the save bar knows that the server does not: the person's local dirty
 * state, and whether a bar is up anywhere.
 *
 * The server holds every change already written (`GET /:id/save-state`).
 * What it cannot see is a field with words typed and not yet saved on blur,
 * a popup form with edits not sent, and a write still on its way. Each of
 * those registers here, per storyboard, so the bar is up from the first
 * keystroke and Save waits for every write to land before it saves.
 *
 * Module state, like the revision book in `use-storyboard.ts`: the writers
 * are scattered over the page (a row, the note, a shot card), and sign-out
 * and the view-as switch, which live outside any storyboard page, must ask
 * the same store whether they may go.
 */
import { createContext, useContext, useEffect, useId, useRef, useSyncExternalStore } from "react";

interface Entry {
  /** Puts the field back to the stored value and drops its kept draft (Discard). */
  revert?: () => void;
}

interface Book {
  dirty: Map<string, Entry>;
  /** Writes called and not yet answered. */
  inFlight: number;
  /** Waiting for `inFlight` to reach zero. */
  idle: Array<() => void>;
  /** Moves on each blocked attempt; the bar shakes when it changes. */
  attention: number;
  /** When a write last landed: until the save state is read after it, the bar stays up. */
  landedAt: number;
}

/** What a component reads, the same object until something changes. */
export interface SaveBarLocal {
  /** Fields or forms holding edits not yet written. */
  dirtyCount: number;
  inFlight: number;
  attention: number;
  landedAt: number;
}

const books = new Map<number, Book>();
const views = new Map<number, SaveBarLocal>();
const listeners = new Set<() => void>();
/** The storyboards whose bar is on screen now. */
const shown = new Set<number>();

const EMPTY: SaveBarLocal = { dirtyCount: 0, inFlight: 0, attention: 0, landedAt: 0 };

function bookOf(id: number): Book {
  let book = books.get(id);
  if (!book) {
    book = { dirty: new Map(), inFlight: 0, idle: [], attention: 0, landedAt: 0 };
    books.set(id, book);
  }
  return book;
}

function emit(id: number): void {
  const book = bookOf(id);
  views.set(id, { dirtyCount: book.dirty.size, inFlight: book.inFlight, attention: book.attention, landedAt: book.landedAt });
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** A local dirty source of one storyboard: on while it holds edits the server does not have. */
export function registerDirty(storyboardId: number, key: string, dirty: boolean, revert?: () => void): void {
  const book = bookOf(storyboardId);
  const had = book.dirty.has(key);
  if (dirty) book.dirty.set(key, { revert });
  else book.dirty.delete(key);
  if (had !== dirty) emit(storyboardId);
}

/** A write on its way: Save waits for it, and the tab asks before closing until it lands. */
export function trackWrite<T>(storyboardId: number, write: Promise<T>): Promise<T> {
  const book = bookOf(storyboardId);
  book.inFlight += 1;
  emit(storyboardId);
  const done = (landed: boolean) => {
    if (landed) book.landedAt = Date.now();
    book.inFlight = Math.max(0, book.inFlight - 1);
    if (book.inFlight === 0) {
      const waiting = book.idle.splice(0);
      for (const resolve of waiting) resolve();
    }
    emit(storyboardId);
  };
  write.then(
    () => done(true),
    () => done(false),
  );
  return write;
}

/** Settles once no write of this storyboard is on its way. */
export function whenIdle(storyboardId: number): Promise<void> {
  const book = bookOf(storyboardId);
  if (book.inFlight === 0) return Promise.resolve();
  return new Promise((resolve) => book.idle.push(resolve));
}

/** Discard: every field puts its stored value back and lets its kept draft go, before the server reverts. */
export function revertLocal(storyboardId: number): void {
  const book = bookOf(storyboardId);
  for (const entry of [...book.dirty.values()]) entry.revert?.();
}

/** The bar's shake and green flash; with no id, every bar on screen. */
export function attention(storyboardId?: number): void {
  const ids = storyboardId === undefined ? [...shown] : [storyboardId];
  for (const id of ids) {
    bookOf(id).attention += 1;
    emit(id);
  }
}

/** The bar says whether it is up, for sign-out, the view-as switch and the navigation blocker. */
export function setShown(storyboardId: number, on: boolean): void {
  if (shown.has(storyboardId) === on) return;
  if (on) shown.add(storyboardId);
  else shown.delete(storyboardId);
  for (const listener of listeners) listener();
}

/** Whether this storyboard's bar is up, live. */
export function useIsShown(storyboardId: number): boolean {
  return useSyncExternalStore(subscribe, () => shown.has(storyboardId));
}

export function isShown(storyboardId: number): boolean {
  return shown.has(storyboardId);
}

/**
 * Run an action that leaves the page some other way than a link (sign-out,
 * view-as, "Ready for editors", leaving edit mode): only when no bar is up;
 * otherwise the bar shakes and nothing happens.
 */
export function guardLeave<T>(action: () => T): T | undefined {
  if (shown.size > 0) {
    attention();
    return undefined;
  }
  return action();
}

/** The local state of one storyboard, live. */
export function useSaveBarLocal(storyboardId: number | null): SaveBarLocal {
  return useSyncExternalStore(subscribe, () => (storyboardId === null ? EMPTY : (views.get(storyboardId) ?? EMPTY)));
}

/* ------------------------------------------------------------------ scope */

/** The storyboard whose save bar the page is under; null outside an editor page (the deck). */
export const SaveBarScope = createContext<number | null>(null);

export function useSaveBarScope(): number | null {
  return useContext(SaveBarScope);
}

/**
 * Register a field or form as dirty while `dirty` is true, under the page's
 * storyboard. `revert` is what Discard calls to drop its local edits. A
 * no-op outside an editor page.
 */
export function useDirtyField(dirty: boolean, revert?: () => void, key?: string): void {
  const storyboardId = useSaveBarScope();
  const own = useId();
  const name = key ?? own;
  const latest = useRef(revert);
  latest.current = revert;
  useEffect(() => {
    if (storyboardId === null) return;
    registerDirty(storyboardId, name, dirty, () => latest.current?.());
    return () => registerDirty(storyboardId, name, false);
  }, [dirty, name, storyboardId]);
}

/** Run an action unless the bar is up, in which case the bar shakes instead. */
export function useUnsavedGuard(storyboardId: number | null) {
  return {
    guard<T>(action: () => T): T | undefined {
      if (storyboardId !== null && shown.has(storyboardId)) {
        attention(storyboardId);
        return undefined;
      }
      return action();
    },
  };
}
